/** Criação da senha definitiva por quem entrou com a senha temporária.
 *
 *  É a única tela alcançável nessa situação: o backend recusa todo o resto com
 *  PASSWORD_CHANGE_REQUIRED, e o guard de rota traz a pessoa para cá. Ao gravar,
 *  o backend devolve um par de tokens novo, que substitui a sessão parcial sem
 *  passar de novo pelo login.
 */
import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useAuth } from '../../core/auth/useAuth'
import { authApi } from '../../core/auth/authApi'
import { homePathFor } from '../../core/router/paths'
import { apiErrorMessage } from '../../shared/apiError'
import { CampoSenhaForte } from '../../shared/form/CampoSenhaForte'
import { avaliar } from '../../shared/form/passwordPolicy'
import { useI18n } from '@/core/i18n/useI18n'
import { AuthLayout } from './AuthLayout'

export function PrimeiroAcessoPage() {
  const { t } = useI18n()
  const { user, status, precisaCriarSenha, aplicarSessao } = useAuth()
  const navigate = useNavigate()
  const [nova, setNova] = useState('')
  const [confirma, setConfirma] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)

  if (status === 'loading') return null
  if (status === 'unauthenticated') return <Navigate to="/login" replace />
  // Sessão já plena: não há senha temporária a substituir.
  if (!precisaCriarSenha) return <Navigate to={homePathFor(user)} replace />

  const dono = { email: user?.email, nome: user?.name }
  const avaliacao = avaliar(nova, dono)
  const naoConfere = confirma.length > 0 && nova !== confirma
  const podeSalvar = avaliacao.valida && nova === confirma && !ocupado

  async function salvar(evento: React.FormEvent) {
    evento.preventDefault()
    if (!podeSalvar) return
    setOcupado(true)
    setErro(null)
    try {
      const sessao = await authApi.definirSenha(nova)
      const usuario = aplicarSessao(sessao)
      navigate(homePathFor(usuario), { replace: true })
    } catch (falha) {
      setErro(apiErrorMessage(falha))
    } finally {
      setOcupado(false)
    }
  }

  return (
    <AuthLayout
      titulo={t('auth.primeiroAcesso.title')}
      subtitulo={t('auth.primeiroAcesso.subtitle')}
    >
      <form onSubmit={salvar} noValidate>
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
            {t('auth.primeiroAcesso.signedInAs')}
            <br />
            <strong style={{ color: 'var(--color-text-strong)' }}>{user?.email}</strong>
          </Typography>

          <CampoSenhaForte
            id="senha-nova"
            label={t('auth.primeiroAcesso.newPassword')}
            value={nova}
            onChange={setNova}
            dono={dono}
            autoFocus
            disabled={ocupado}
          />

          <TextField
            id="senha-confirma"
            label={t('auth.primeiroAcesso.confirmPassword')}
            type="password"
            size="small"
            value={confirma}
            onChange={(e) => setConfirma(e.target.value)}
            autoComplete="new-password"
            disabled={ocupado}
            error={naoConfere}
            helperText={naoConfere ? t('auth.primeiroAcesso.mismatch') : undefined}
            fullWidth
          />

          {erro && (
            <div className="login-error" role="alert">
              {erro}
            </div>
          )}

          <Button type="submit" variant="contained" size="large" disabled={!podeSalvar}>
            {ocupado ? t('auth.primeiroAcesso.saving') : t('auth.primeiroAcesso.save')}
          </Button>
        </Stack>
      </form>
    </AuthLayout>
  )
}
